import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useActiveOrganization } from './useActiveOrganization';
import { toast } from 'sonner';
import { updateBudgetAmountPaid } from '@/utils/budgetTransactions';
import { useQueryClient } from '@tanstack/react-query';

export interface Transaction {
  id: string;
  type: string;
  category: string;
  description: string;
  amount: number;
  originalAmount?: number;
  paidAmount?: number;
  date: string;
  patientName?: string;
  paymentMethod?: string;
  status?: string;
  dueDate?: string;
  createdAt: string;
  budgetId?: string;
  installmentNumber?: number;
  totalInstallments?: number;
  parentTransactionId?: string;
  hasPartialPayments?: boolean;
  isInternalRevenue?: boolean;
  costCategory?: string;
  employeeName?: string;
}

export function useTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const queryClient = useQueryClient();

  const invalidateRelatedCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['finance-stats'] });
    queryClient.invalidateQueries({ queryKey: ['finance-monthly'] });
    queryClient.invalidateQueries({ queryKey: ['finance-category'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['delinquency-alerts'] });
  };

  const fetchTransactions = async () => {
    if (!user || !activeOrganizationId) return;
    
    try {
      setLoading(true);
      
      // Fetch parent transactions
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('organization_id', activeOrganizationId)
        .is('parent_transaction_id', null)
        .order('date', { ascending: false });

      if (error) throw error;

      // Get IDs of all parent transactions that have child payments
      const parentIds = (data || []).map(t => t.id);
      const { data: childPayments, error: childError } = await supabase
        .from('transactions')
        .select('parent_transaction_id')
        .eq('organization_id', activeOrganizationId)
        .in('parent_transaction_id', parentIds);

      if (childError) throw childError;

      // Create a set of parent IDs that have partial payments
      const parentsWithPayments = new Set(
        (childPayments || []).map(c => c.parent_transaction_id)
      );

      const formattedTransactions: Transaction[] = (data || []).map(t => ({
        id: t.id,
        type: t.type,
        category: t.category,
        description: t.description,
        amount: t.amount,
        originalAmount: t.original_amount ?? t.amount,
        paidAmount: t.paid_amount ?? 0,
        date: t.date,
        patientName: t.patient_name || undefined,
        paymentMethod: t.payment_method || undefined,
        status: t.status || 'paid',
        dueDate: t.due_date || undefined,
        createdAt: t.created_at || '',
        budgetId: t.budget_id || undefined,
        installmentNumber: t.installment_number || undefined,
        totalInstallments: t.total_installments || undefined,
        parentTransactionId: t.parent_transaction_id || undefined,
        hasPartialPayments: parentsWithPayments.has(t.id),
        isInternalRevenue: t.is_internal_revenue ?? true,
        costCategory: t.cost_category || undefined,
        employeeName: t.employee_name || undefined,
      }));

      setTransactions(formattedTransactions);
    } catch (error: any) {
      toast.error('Erro ao carregar transações');
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = async (transactionData: Omit<Transaction, 'id' | 'createdAt'>) => {
    if (!user || !activeOrganizationId) return null;

    try {
      const originalAmount = transactionData.originalAmount || transactionData.amount;
      const paidAmount = transactionData.status === 'paid' ? originalAmount : 0;

      const { data, error } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          organization_id: activeOrganizationId,
          type: transactionData.type,
          category: transactionData.category,
          description: transactionData.description,
          amount: transactionData.amount,
          original_amount: originalAmount,
          paid_amount: paidAmount,
          date: transactionData.date,
          patient_name: transactionData.patientName,
          payment_method: transactionData.paymentMethod,
          status: transactionData.status || 'paid',
          due_date: transactionData.dueDate,
          budget_id: transactionData.budgetId,
          installment_number: transactionData.installmentNumber,
          total_installments: transactionData.totalInstallments,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Transação adicionada com sucesso!');
      await fetchTransactions();
      invalidateRelatedCaches();
      return data;
    } catch (error: any) {
      toast.error('Erro ao adicionar transação');
      console.error('Error adding transaction:', error);
      return null;
    }
  };

  const updateTransaction = async (id: string, updates: Partial<Transaction>) => {
    if (!user) return;

    try {
      // Get current transaction to check budget_id
      const currentTransaction = transactions.find(t => t.id === id);
      
      const updateData: any = {};
      
      if (updates.type !== undefined) updateData.type = updates.type;
      if (updates.category !== undefined) updateData.category = updates.category;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.amount !== undefined) updateData.amount = updates.amount;
      if (updates.date !== undefined) updateData.date = updates.date;
      if (updates.patientName !== undefined) updateData.patient_name = updates.patientName;
      if (updates.paymentMethod !== undefined) updateData.payment_method = updates.paymentMethod;
      if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate;
      if (updates.budgetId !== undefined) updateData.budget_id = updates.budgetId;
      if (updates.installmentNumber !== undefined) updateData.installment_number = updates.installmentNumber;
      if (updates.totalInstallments !== undefined) updateData.total_installments = updates.totalInstallments;
      
      // If marking as paid, update paid_amount to full original_amount
      if (updates.status === 'paid') {
        updateData.status = 'paid';
        const originalAmount = currentTransaction?.originalAmount || currentTransaction?.amount || 0;
        updateData.paid_amount = originalAmount;
      } else if (updates.status !== undefined) {
        updateData.status = updates.status;
      }

      const { error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // If transaction is related to a budget and status changed to paid,
      // update budget's amount_paid
      const budgetId = updates.budgetId || currentTransaction?.budgetId;
      if (budgetId && updates.status === 'paid') {
        console.log('💰 Transaction marked as paid, updating budget amount_paid');
        await updateBudgetAmountPaid(budgetId);
      }

      toast.success('Transação atualizada com sucesso!');
      await fetchTransactions();
      invalidateRelatedCaches();
    } catch (error: any) {
      toast.error('Erro ao atualizar transação');
      console.error('Error updating transaction:', error);
    }
  };

  const deleteTransaction = async (id: string) => {
    if (!user) return;

    try {
      // First: delete all child transactions (partial payments) linked to this transaction
      const { error: childError } = await supabase
        .from('transactions')
        .delete()
        .eq('parent_transaction_id', id);

      if (childError) throw childError;

      // Then: delete the main transaction
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Transação removida com sucesso!');
      await fetchTransactions();
      invalidateRelatedCaches();
    } catch (error: any) {
      toast.error('Erro ao remover transação');
      console.error('Error deleting transaction:', error);
    }
  };

  const getTransaction = (id: string) => {
    return transactions.find(t => t.id === id);
  };

  const getTransactionById = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching transaction by id:', error);
      return null;
    }
  };

  const getRelatedPayments = async (transactionId: string) => {
    if (!user || !activeOrganizationId) return [];
    
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('organization_id', activeOrganizationId)
        .eq('parent_transaction_id', transactionId)
        .order('date', { ascending: false });

      if (error) throw error;

      return (data || []).map(t => ({
        id: t.id,
        type: t.type,
        category: t.category,
        description: t.description,
        amount: t.amount,
        date: t.date,
        patientName: t.patient_name || undefined,
        paymentMethod: t.payment_method || undefined,
        status: t.status || 'paid',
        dueDate: t.due_date || undefined,
        createdAt: t.created_at || '',
        budgetId: t.budget_id || undefined,
        installmentNumber: t.installment_number || undefined,
        totalInstallments: t.total_installments || undefined,
      }));
    } catch (error: any) {
      console.error('Error fetching related payments:', error);
      return [];
    }
  };

  const addPartialPayment = async (
    originalTransactionId: string, 
    paymentValue: number, 
    paymentDate: string,
    paymentMethod: string
  ) => {
    if (!user || !activeOrganizationId) return null;

    try {
      // Get original transaction from database to ensure fresh data
      const { data: originalData, error: fetchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', originalTransactionId)
        .single();

      if (fetchError) throw fetchError;
      if (!originalData) throw new Error('Transação original não encontrada');

      const originalAmount = originalData.original_amount ?? originalData.amount;
      const currentPaidAmount = originalData.paid_amount ?? 0;
      const newPaidAmount = currentPaidAmount + paymentValue;
      const remaining = originalAmount - newPaidAmount;

      // Create payment record linked to original transaction
      const { data: newPayment, error: paymentError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          organization_id: activeOrganizationId,
          type: 'receita',
          category: originalData.category,
          description: `Pagamento parcial - ${originalData.description}`,
          amount: paymentValue,
          original_amount: paymentValue,
          paid_amount: paymentValue,
          date: paymentDate,
          patient_name: originalData.patient_name,
          payment_method: paymentMethod,
          status: 'paid',
          budget_id: originalData.budget_id,
          parent_transaction_id: originalTransactionId,
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Update original transaction's paid_amount and status
      const newStatus = remaining <= 0 ? 'paid' : 'pending';
      
      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          paid_amount: newPaidAmount,
          status: newStatus,
        })
        .eq('id', originalTransactionId);

      if (updateError) throw updateError;

      // If related to a budget, update budget's amount_paid
      if (originalData.budget_id) {
        await updateBudgetAmountPaid(originalData.budget_id);
      }

      if (remaining <= 0) {
        toast.success('Pagamento quitado completamente!');
      } else {
        toast.success(`Pagamento parcial registrado! Saldo restante: R$ ${remaining.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
      }
      
      await fetchTransactions();
      invalidateRelatedCaches();
      return newPayment;
    } catch (error: any) {
      toast.error('Erro ao registrar pagamento parcial');
      console.error('Error adding partial payment:', error);
      return null;
    }
  };

  useEffect(() => {
    if (user && activeOrganizationId) {
      fetchTransactions();
    }
  }, [user, activeOrganizationId]);

  return {
    transactions,
    loading,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    getTransaction,
    getTransactionById,
    getRelatedPayments,
    addPartialPayment,
    refreshTransactions: fetchTransactions,
  };
}
